import React, { useState } from "react";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import Table from "../../components/common/Table";
import Modal from "../../components/common/Modal";
import { PencilIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import UserForm from "../../components/forms/UserForm";
import { useUsersInfinite } from "../../hooks/useUsersInfinite";
import { useDebounce } from "../../hooks/useDebounce";
import {
  User,
  UserCreateFormData,
  UserUpdateFormData,
} from "../../schemas/userSchema";
import { useAuthPermissions } from "../../hooks/useAuthPermissions";
import { getChangedFields } from "../../utils/formUtils";

const UserList: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);

  const {
    users,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    createUser,
    updateUser,
    deleteUsers,
  } = useUsersInfinite({ search: debouncedSearch || undefined });
  const { canEdit, canDelete, canCreate, canView } =
    useAuthPermissions("users");

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleDelete = (user: User) => {
    if (window.confirm(`Are you sure you want to delete ${user.name}?`)) {
      deleteUsers([user.id]);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (window.confirm(`Are you sure you want to delete ${ids.length} user(s)?`)) {
      deleteUsers(ids);
      setRowSelection({});
    }
  };

  const handleSubmit = (data: UserCreateFormData | UserUpdateFormData) => {
    if (selectedUser) {
      // Only send changed fields for update
      const changedFields = getChangedFields(data as UserUpdateFormData, {
        name: selectedUser.name,
        username: selectedUser.username,
        email: selectedUser.email,
        phoneNumber: selectedUser.phoneNumber || "",
        roleId: selectedUser.roleId || "",
      });
      updateUser({
        id: selectedUser.id,
        user: changedFields,
      });
    } else {
      createUser(data as UserCreateFormData);
    }
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleToggleActive = (user: User) => {
    updateUser({
      id: user.id,
      user: {
        isActive: !user.isActive,
      },
    });
  };

  const columns: ColumnDef<User>[] = [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "username", header: "Username" },
    { accessorKey: "email", header: "Email" },
    { accessorKey: "phoneNumber", header: "Phone Number" },
    { accessorKey: "role", header: "Role" },
    {
      id: "isActive",
      header: "Status",
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className="flex items-center">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={user.isActive}
                onChange={() => handleToggleActive(user)}
                disabled={!canEdit}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-default/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-default peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
              <span className="ml-3 text-sm font-medium text-gray-700">
                {user.isActive ? "Active" : "Inactive"}
              </span>
            </label>
          </div>
        );
      },
    },
  ];

  const actions = [
    {
      label: "Edit",
      icon: <PencilIcon className="h-4 w-4" />,
      onClick: handleEdit,
      variant: "success" as const,
      visible: canEdit,
    },
    {
      label: "Delete",
      icon: <TrashIcon className="h-4 w-4" />,
      onClick: handleDelete,
      variant: "danger" as const,
      visible: canDelete,
    },
  ];

  return (
    <div className="flex flex-col px-6 py-2">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl font-bold text-gray-800">Users</h1>
        {canCreate && (
          <button
            onClick={() => {
              setSelectedUser(null);
              setIsModalOpen(true);
            }}
            className="p-2 bg-primary-default text-white rounded hover:bg-primary-light"
          >
            <PlusIcon className="h-6 w-6" />
          </button>
        )}
      </div>

      {canView && (
        <Table
          data={users as User[]}
          columns={columns}
          actions={actions}
          searchable={true}
          onSearch={setSearch}
          pagination={true}
          isLoading={isLoading}
          maxHeight="67vh"
          infinite
          hasNextPage={hasNextPage}
          isFetchingMore={isFetchingNextPage}
          onLoadMore={() => fetchNextPage()}
          selectable={canDelete}
          getRowId={(item) => item.id}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          bulkActions={
            canDelete
              ? [
                  {
                    label: "Delete Selected",
                    icon: <TrashIcon className="h-4 w-4" />,
                    onClick: handleBulkDelete,
                    variant: "danger" as const,
                  },
                ]
              : undefined
          }
        />
      )}

      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleCancel}
          title={selectedUser ? "Edit User" : "Create User"}
          size="full"
        >
          <UserForm
            initialData={selectedUser || undefined}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={false}
            isUpdate={selectedUser !== null}
            isOpen={isModalOpen}
          />
        </Modal>
      )}
    </div>
  );
};

export default UserList;
